import * as React from "react"
import { Control, Emitter } from 'rete'
import { EventsTypes } from "rete/types/events"

interface SelectControlProps {
    emitter: Emitter<EventsTypes> | null
    getId: () => string | undefined
    setId: (x: string) => {}
    getOptions: () => Array<SelectControlOptions>
    change: () => void
    label: string | undefined
}

export interface SelectControlOptions {
    id: number
    name: string
    gridtype? : string
    /** Optional type label rendered as a socket-style badge (e.g. Elevation, Canopy). */
    badge?: string
}

type EmitterWithOff = Emitter<EventsTypes> & {
    off: (event: string, handler: (...args: unknown[]) => void) => void
}

const hasOff = (emitter: Emitter<EventsTypes> | null): emitter is EmitterWithOff =>
    !!emitter && typeof (emitter as Partial<EmitterWithOff>).off === "function"

export function TypeBadge({ label }: { label: string }) {
    return (
        <span className="select-control-dropdown__badge">{label}</span>
    )
}

const SelectInput = ({ emitter, getId, setId, getOptions, change, label }: SelectControlProps) => {

    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
    const [, forceUpdate] = React.useReducer(x => x + 1, 0)
    const [isOpen, setIsOpen] = React.useState(false)
    const dropdownRef = React.useRef<HTMLDivElement>(null)

    // Listen for process events to force re-render when options might have changed
    React.useEffect(() => {
        if (!emitter) return
        
        const handler = () => {
            // Small delay to ensure node data is updated
            setTimeout(() => forceUpdate(), 0)
        }
        
        emitter.on("process", handler)
        return () => {
            if (hasOff(emitter)) {
                emitter.off("process", handler)
            }
        }
    }, [emitter])

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside)
        }
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [isOpen])

    const stopPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptionId = event.target.value
        setId(selectedOptionId)
        change()
        forceUpdate()

        emitter?.trigger("process")
    }

    const options = getOptions()
    const selectedId = getId()
    const selected = options.find(opt => String(opt.id) === String(selectedId)) ?? options[0]
    const hasBadges = options.some(opt => opt.badge)

    const selectOption = (id: number) => {
        setId(String(id))
        change()
        forceUpdate()
        setIsOpen(false)
        emitter?.trigger("process")
    }

    const stopWheel = (event: React.WheelEvent) => {
        event.stopPropagation()
    }

    if (hasBadges) {
        return (
            <div
                className={`select-control-dropdown${isOpen ? " is-open" : ""}`}
                ref={dropdownRef}
            >
                {label && (
                    <label className="select-control-dropdown__label">{label}</label>
                )}
                <button
                    type="button"
                    className="select-control-dropdown__trigger"
                    title={selected?.name}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    onClick={(event) => {
                        event.stopPropagation()
                        setIsOpen(!isOpen)
                    }}
                    onPointerDown={stopPropagation}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                >
                    {selected?.badge && <TypeBadge label={selected.badge} />}
                    <span className="select-control-dropdown__trigger-name">{selected?.name}</span>
                    <span className="select-control-dropdown__caret" aria-hidden />
                </button>
                {isOpen && (
                    <div
                        className="select-control-dropdown__menu"
                        role="listbox"
                        onMouseDown={stopPropagation}
                        onPointerDown={stopPropagation}
                        onWheel={stopWheel}
                    >
                        {options.map(opt => {
                            const isSelected = String(opt.id) === String(selectedId)
                            return (
                                <button
                                    type="button"
                                    key={opt.id}
                                    role="option"
                                    aria-selected={isSelected}
                                    title={opt.name}
                                    className={`select-control-dropdown__option${isSelected ? " is-selected" : ""}`}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        selectOption(opt.id)
                                    }}
                                    onMouseDown={stopPropagation}
                                    onPointerDown={stopPropagation}
                                >
                                    {opt.badge && <TypeBadge label={opt.badge} />}
                                    <span className="select-control-dropdown__option-name">{opt.name}</span>
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        )
    }
    
    if(options[0]?.gridtype) {
        const numeric = options.filter(opt => opt.gridtype == "NumericTileGrid")
        const categorical = options.filter(opt => opt.gridtype == "CategoricalTileGrid")
        const boolean = options.filter(opt => opt.gridtype == "BooleanTileGrid")

        return (
            <label style={{ display: "block" }}>
                {label}
                <select
                    className="custom-select d-block"
                    style={{ maxWidth: "400px" }}
                    onChange={onChange}
                    onPointerDown={stopPropagation}
                    onPointerUp={stopPropagation}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                    value={getId()}
                >
                    <optgroup label="Numeric">
                        {
                            numeric.map(opt =>
                                <option value={opt.id} key={opt.id}>
                                    {opt.name}
                                </option>
                            )
                        }
                    </optgroup>
                    <optgroup label="Categorical">
                        {
                            categorical.map(opt =>
                                <option value={opt.id} key={opt.id}>
                                    {opt.name}
                                </option>
                            )
                        }
                    </optgroup>
                    <optgroup label="Boolean">
                        {
                            boolean.map(opt =>
                                <option value={opt.id} key={opt.id}>
                                    {opt.name}
                                </option>
                            )
                        }
                    </optgroup>
                </select>
            </label>
        )

    }else{
        return (
            <label style={{ display: "block" }}>
                {label}
                <select
                    className="custom-select d-block"
                    style={{ maxWidth: "400px" }}
                    onChange={onChange}
                    onPointerDown={stopPropagation}
                    onPointerUp={stopPropagation}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                    value={getId()}
                >
                    {
                        options.map(opt =>
                            <option value={opt.id} key={opt.id}>
                                {opt.name}
                            </option>
                        )
                    }
                </select>
            </label>
        )
    }

}


export class SelectControl extends Control {
    props: SelectControlProps
    component: (props: SelectControlProps) => JSX.Element
    type: string
    placement?: string

    constructor(emitter: Emitter<EventsTypes> | null, key: string, getOptions: () => Array<SelectControlOptions>, change: () => void, label: string | undefined = undefined, placement?: string) {
        super(key)
        this.type = 'SelectControl'
        if (placement) this.placement = placement

        this.props = {
            emitter,
            getId: this.getData.bind(this, key),
            setId: this.putData.bind(this, key),
            getOptions,
            change,
            label
        }

        this.component = SelectInput
    }
}
